import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui';
import { useHomeReadings } from '@/features/home-readings/api';
import {
  KIND_LABEL,
  OUT_OF_RANGE_LABEL,
  SOURCE_LABEL,
  readingValue,
} from '@/features/home-readings/format';
import {
  READING_KINDS,
  type ReadingKind,
  type ReadingsPage,
} from '@/features/home-readings/types';
import { isApiError } from '@/lib/api';
import { ReadingsChart } from './ReadingsChart';
import { ReadingThresholdsDrawer } from './ReadingThresholdsDrawer';
import { when } from './rx-request-format';
import styles from './HomeReadings.module.css';

const TREND: Record<'up' | 'down' | 'steady', string> = {
  up: 'Going up',
  down: 'Going down',
  steady: 'Steady',
};

/**
 * "Home readings" on the patient record (RL-1141): the readings the patient
 * sends from the app (RL-997), blood pressure or glucose, with the 30-day
 * summary, a chart, the list (newest first, cursor pages) and the alert
 * thresholds, which the care team can edit (`chronic_care:manage`).
 */
export function HomeReadingsCard({
  patientId,
  canManage,
}: {
  patientId: string;
  canManage: boolean;
}) {
  const [kind, setKind] = useState<ReadingKind>('blood_pressure');
  const [editing, setEditing] = useState(false);
  const q = useHomeReadings(patientId, kind);
  const first = q.data?.pages[0];
  const rows = q.data?.pages.flatMap((p) => p.readings) ?? [];
  const notCared = isApiError(q.error) && q.error.code === 'PATIENT_NOT_FOUND';

  return (
    <Card
      title="Home readings"
      actions={
        <ChipToggle
          label="Reading type"
          single
          options={READING_KINDS.map((k) => ({
            value: k,
            label: KIND_LABEL[k],
          }))}
          selected={[kind]}
          onChange={([k]) => k && setKind(k)}
        />
      }
    >
      {q.isLoading ? (
        <LoadingState label="Loading home readings" />
      ) : notCared ? (
        <p className={styles.muted}>
          Home readings are shown to the care team of a branch where the patient
          is registered, on a chronic care register or alerted.
        </p>
      ) : q.isError || !first ? (
        <ErrorState
          error={q.error}
          title="Could not load the home readings"
          onRetry={() => void q.refetch()}
        />
      ) : (
        <div className={styles.drawer}>
          <Summary page={first} />
          <Thresholds
            page={first}
            canManage={canManage}
            onEdit={() => setEditing(true)}
          />
          {rows.length > 0 && (
            <ReadingsChart
              kind={kind}
              rows={rows}
              band={first.chartBand}
              thresholds={first.alertThresholds}
            />
          )}
          <DataTable
            caption={`${KIND_LABEL[kind]} readings from the patient app`}
            rows={rows}
            rowKey={(r) => r.id}
            empty={
              <EmptyState
                title={`No ${KIND_LABEL[kind].toLowerCase()} readings yet`}
                body="Readings the patient sends from the app appear here."
              />
            }
            columns={[
              {
                key: 'at',
                header: 'Measured',
                render: (r) => when(r.measuredAt),
              },
              {
                key: 'value',
                header: 'Reading',
                render: (r) => <strong>{readingValue(r)}</strong>,
              },
              {
                key: 'flag',
                header: 'Range',
                render: (r) =>
                  r.outOfRange ? (
                    <Badge tone="danger">
                      {OUT_OF_RANGE_LABEL[r.outOfRange]}
                    </Badge>
                  ) : (
                    <span className={styles.muted}>Within thresholds</span>
                  ),
              },
              {
                key: 'source',
                header: 'Source',
                render: (r) =>
                  `${SOURCE_LABEL[r.source]}${r.deviceName ? ` · ${r.deviceName}` : ''}`,
              },
            ]}
          />
          {q.hasNextPage && (
            <Button
              size="sm"
              loading={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              Load older readings
            </Button>
          )}
        </div>
      )}
      {editing && (
        <ReadingThresholdsDrawer
          patientId={patientId}
          onClose={() => setEditing(false)}
        />
      )}
    </Card>
  );
}

function Summary({ page }: { page: ReadingsPage }) {
  const s = page.stats;
  const bp = page.kind === 'blood_pressure';
  const avg = bp
    ? s.average.systolic !== null
      ? `${Math.round(s.average.systolic)}/${Math.round(s.average.diastolic ?? 0)} mmHg`
      : '—'
    : s.average.value !== null
      ? `${s.average.value} mmol/L`
      : '—';
  return (
    <dl className={styles.stats} aria-label={`Last ${s.days} days`}>
      <div>
        <dt>Readings ({s.days} days)</dt>
        <dd>{s.count}</dd>
      </div>
      <div>
        <dt>Average</dt>
        <dd>{avg}</dd>
      </div>
      <div>
        <dt>{bp ? 'Systolic range' : 'Range'}</dt>
        <dd>
          {s.lowest !== null && s.highest !== null
            ? `${s.lowest}–${s.highest}`
            : '—'}
        </dd>
      </div>
      <div>
        <dt>Out of range</dt>
        <dd>{s.outOfRangeCount}</dd>
      </div>
      <div>
        <dt>Trend (last 7 days)</dt>
        <dd>{s.trend ? TREND[s.trend] : '—'}</dd>
      </div>
    </dl>
  );
}

function Thresholds({
  page,
  canManage,
  onEdit,
}: {
  page: ReadingsPage;
  canManage: boolean;
  onEdit: () => void;
}) {
  const t = page.alertThresholds;
  return (
    <div className={styles.thresholds}>
      <span>
        <strong>Alert thresholds</strong>{' '}
        {page.kind === 'blood_pressure'
          ? `at or above ${t.bpSystolicHigh}/${t.bpDiastolicHigh} mmHg`
          : `below ${t.glucoseLow} or above ${t.glucoseHigh} mmol/L`}{' '}
        · {t.setByCareTeam ? 'set by the care team' : 'clinic defaults'}
      </span>
      {canManage && (
        <Button size="sm" onClick={onEdit}>
          Edit thresholds
        </Button>
      )}
    </div>
  );
}
